import {
    IEventEmitter,
    IVibrationManager,
    TypesAudio,
    TypesCore,
    TypesSocial
} from "@dp-instant/interfaces";
import {lazyGet, WaitAction} from "@dp-instant/core";
import {Localize} from "@dp-instant/i18n";
import {
    Button,
    ComponentClassName,
    UIEvents,
    useEventListener,
    useInjection,
    VersionLabel,
    visibilityEffect
} from "@dp-instant/ui";
import {useCallback, useState} from "preact/hooks";
import cn from "classnames";
import {BackButton} from "../components/BackButton/BackButton";
import {UIConstants} from "../constants";
import {SVG} from "../images";
import "./SettingsPopup.less";
import {JSX} from "preact/jsx-runtime";
import {AudioModel} from "@dp-instant/audio";
import {playUIClickSound} from "@game/helpers";
import {UserIdLabel} from "../components/UserStatusInfo/UserIdLabel";
import {SIOConstants} from "@game/constants";
import {apiClient} from "@game/api/ApiClient";
import {hapticSettings, hapticMedium} from "@game/haptic";

// --- Toggle button (existing pattern) ---
type ToggleButtonProps = JSX.DOMAttributes<HTMLDivElement> &
    ComponentClassName & {toggled?: boolean; onToggled?: (on: boolean) => any; label?: string};
const ToggleButton = (props: ToggleButtonProps) => {
    const [state, setState] = useState(props.toggled);
    const toggle = () => {
        props.onToggled && props.onToggled(!state);
        playUIClickSound();
        hapticMedium();
        setState(!state);
    };
    return (
        <div className="settings-row">
            {props.label && <span className="settings-row__label">{props.label}</span>}
            <Button className={cn("toggle-btn", props.className, {toggled: state})} onClick={toggle}>
                {props.children}
            </Button>
        </div>
    );
};

// --- Language segment control ---
const LanguageSwitch = ({onChange}: {onChange: () => void}) => {
    const isRu = Localize.defaultLocale.startsWith("ru");
    const [lang, setLang] = useState<"ru" | "en">(isRu ? "ru" : "en");
    const pick = (l: "ru" | "en") => {
        if (l === lang) return;
        playUIClickSound();
        hapticMedium();
        const locale = l === "ru" ? "ru_RU" : "en_US";
        Localize.defaultLocale = locale;
        localStorage.setItem("sio_lang", locale);
        setLang(l);
        onChange();
    };
    return (
        <div className="settings-row">
            <span className="settings-row__label">{Localize.get("ui-settings-language", "LANGUAGE")}</span>
            <div className="lang-switch">
                <button className={cn("lang-switch__btn", {active: lang === "ru"})} onClick={() => pick("ru")}>RU</button>
                <button className={cn("lang-switch__btn", {active: lang === "en"})} onClick={() => pick("en")}>EN</button>
            </div>
        </div>
    );
};

// --- Fullscreen toggle (TG only) ---
const FullscreenToggle = () => {
    const tg = (window as any).Telegram?.WebApp;
    const [on, setOn] = useState(localStorage.getItem("sio_fullscreen") !== "0");
    const toggle = () => {
        playUIClickSound();
        hapticMedium();
        const next = !on;
        setOn(next);
        localStorage.setItem("sio_fullscreen", next ? "1" : "0");
        try {
            if (next) tg?.requestFullscreen?.();
            else tg?.exitFullscreen?.();
        } catch (e) { /* unsupported */ }
    };
    return (
        <div className="settings-row">
            <span className="settings-row__label">{Localize.get("ui-settings-fullscreen", "FULLSCREEN")}</span>
            <Button className={cn("toggle-btn toggle-btn--small", {toggled: !on})} onClick={toggle}>
                <span className="toggle-btn__text">{on ? "ON" : "OFF"}</span>
            </Button>
        </div>
    );
};

export const SettingsPopup = () => {
    const [, updateState] = useState({});
    const forceUpdate = useCallback(() => updateState({}), []);
    const [invisible, setInvisible] = visibilityEffect(UIConstants.popup.startDelay);
    const dispatcher: IEventEmitter = useInjection(TypesCore.dispatcher);
    const audioModel: AudioModel = useInjection(TypesAudio.model);
    const vibrationManager: IVibrationManager | undefined = lazyGet(TypesSocial.vibrationManager);
    const {IN_GAME_AUDIO_CONTROL_ALLOWED} = SIOConstants;
    const isTg = __SOCIAL__ === "tg";
    if (isTg) apiClient.trackFunnelEvent("settings_opened");

    useEventListener(AudioModel.MUTE_SOUNDS, () => forceUpdate, []);
    useEventListener(AudioModel.MUTE_MUSIC, () => forceUpdate, []);
    const close = async () => {
        setInvisible(true);
        await WaitAction.ms(300);
        dispatcher.emit(UIEvents.POPUP, {id: null});
    };
    return (
        <div className="popups" onClick={e => e.target === e.currentTarget && close()}>
            <BackButton className="white" onClick={close} />
            <div className={cn("popup-settings")}>
                <div className={cn("popup", {invisible})}>
                    <div className="popup__title">{Localize.get("ui-settings-title", "SETTINGS")}</div>
                    <div className={cn("popup__body")}>
                        {/* Language */}
                        <LanguageSwitch onChange={() => {
                            forceUpdate();
                            dispatcher.emit("LOCALE_CHANGED");
                        }} />

                        {/* Sound */}
                        {IN_GAME_AUDIO_CONTROL_ALLOWED ? (
                            <ToggleButton
                                label={Localize.get("ui-settings-sound", "SOUND")}
                                toggled={audioModel.soundsMuted()}
                                onToggled={(on: boolean) => audioModel.muteSounds(on)}
                            >
                                <SVG.Sounds />
                            </ToggleButton>
                        ) : null}

                        {/* Vibration — two sub-toggles (TG only) */}
                        {isTg ? (
                            <div className="settings-section">
                                <div className="settings-section__title">{Localize.get("ui-settings-vibration", "VIBRATION")}</div>
                                <ToggleButton
                                    label={Localize.get("ui-settings-vibration-buttons", "Buttons")}
                                    toggled={!hapticSettings.buttons}
                                    onToggled={(muted: boolean) => { hapticSettings.buttons = !muted; }}
                                >
                                    <SVG.Vibrate />
                                </ToggleButton>
                                <ToggleButton
                                    label={Localize.get("ui-settings-vibration-battle", "Battle")}
                                    toggled={!hapticSettings.battle}
                                    onToggled={(muted: boolean) => { hapticSettings.battle = !muted; }}
                                >
                                    <SVG.Vibrate />
                                </ToggleButton>
                            </div>
                        ) : vibrationManager ? (
                            <ToggleButton
                                label={Localize.get("ui-settings-vibration", "VIBRATION")}
                                toggled={!vibrationManager.enabled}
                                onToggled={(on: boolean) => {
                                    vibrationManager.enabled = !on;
                                    vibrationManager.enabled && vibrationManager.vibrate();
                                }}
                            >
                                <SVG.Vibrate />
                            </ToggleButton>
                        ) : null}

                        {/* Fullscreen (TG only) */}
                        {isTg && <FullscreenToggle />}
                    </div>
                    <UserIdLabel />
                </div>
                <VersionLabel />
            </div>
        </div>
    );
};
